import { SectionHeading } from "@/components/site/SectionHeading";
import { LeadForm } from "@/components/site/LeadForm";
import { useSeo } from "@/hooks/useSeo";

const steps = [
  {
    title: "A free valuation",
    body: "We benchmark your property against recent comparable sales in your community and send back a clear, honest valuation. No pressure, no jargon.",
  },
  {
    title: "Premium presentation",
    body: "Cinematic photography, drone footage, floorplans and a dedicated landing page, all produced and paid for by us. Your home, looking its absolute best.",
  },
  {
    title: "Targeted exposure",
    body: "If you decide to move forward, we match your home to qualified clients in our Dubai, GCC and European network.",
  },
  {
    title: "We handle the rest",
    body: "From offer negotiation to NOC, transfer and key handover, we're with YOU every step of the way. The boring bits (and the tricky bits), sorted.",
  },
];

export function ListPropertyPage() {
  useSeo({
    title: "Property Valuation with Hive Homes Dubai",
    description:
      "Request a private Dubai property valuation with Hive Homes. Clear pricing guidance, premium presentation advice, and a focused next-step plan.",
  });

  return (
    <>
      <section className="border-b border-[var(--line)] bg-[var(--bg-2)]">
        <div className="container-wide py-12 md:py-16 short:py-8">
          <SectionHeading
            as="h1"
            eyebrow="List with Hive"
            size="xl"
            title="Request a private valuation."
            body="Share a few details and we will benchmark your home against recent Dubai comparables, then come back with clear pricing guidance."
          />
        </div>
      </section>

      <section className="container-wide grid gap-10 py-10 md:grid-cols-12 md:gap-14 md:py-16 short:py-8">
        <div className="md:col-span-7">
          <p className="eyebrow text-[var(--cream)]">How it works</p>
          <ul className="mt-6 border-t border-[var(--line)] sm:mt-8">
            {steps.map((s) => (
              <li
                key={s.title}
                className="grid grid-cols-1 gap-2 border-b border-[var(--line)] py-5 sm:gap-3 sm:py-6 md:grid-cols-[260px_1fr] md:gap-10 md:py-7"
              >
                <p
                  className="font-display text-[22px] leading-tight text-[var(--text)] sm:text-[26px] md:text-[36px]"
                  style={{ fontVariationSettings: '"opsz" 72' }}
                >
                  {s.title}
                </p>
                <p className="text-[14.5px] leading-relaxed text-[var(--text-soft)] sm:text-[15.5px]">
                  {s.body}
                </p>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-5">
          <div className="border border-[var(--line-strong)] bg-[var(--surface)] p-5 sm:p-6 md:sticky md:top-32 md:p-8">
            <p className="eyebrow text-[var(--cream)]">Tell us about it</p>
            <h2
              className="mt-4 font-display text-[24px] leading-[1.1] text-[var(--text)] sm:mt-5 sm:text-[28px] sm:leading-[1.05] md:text-[40px]"
              style={{ fontVariationSettings: '"opsz" 72' }}
            >
              A few details, and we'll take it from there.
            </h2>
            <p className="mt-3 text-[14px] text-[var(--text-soft)]">
              Drop your property details below. We'll be back with a valuation
              within 24 hours.
            </p>
            <div className="mt-8">
              <LeadForm
                source="list-property"
                cta="Get my valuation"
                successMessage="Thanks! One of our team will be in touch within 24 hours."
                useFullContactFields={false}
                messageLabel="Anything we should know?"
                extraFields={[
                  { name: "address", label: "Property address", placeholder: "Tower / villa, community", required: true },
                  { name: "propertyType", label: "Property type", placeholder: "Apartment / Villa / Townhouse" },
                  { name: "bedrooms", label: "Bedrooms", placeholder: "e.g. 3" },
                  { name: "askingPrice", label: "Asking price (AED)", placeholder: "e.g. 5,000,000" },
                ]}
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
